import { useEffect, useRef, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { pb, markActivity, LAST_ACTIVITY_KEY } from '../lib/pocketbase'
import { showToast } from '../components/Toast'

// Default timeout: 60 minutes
const DEFAULT_TIMEOUT_MS = 60 * 60 * 1000
// Warning before timeout: 5 minutes
const WARNING_BEFORE_MS = 5 * 60 * 1000
// Real input is recorded at most this often.
const ACTIVITY_THROTTLE_MS = 30 * 1000

interface UseSessionTimeoutOptions {
  timeoutMs?: number
  warningMs?: number
  onTimeout?: () => void
  onWarning?: () => void
  enabled?: boolean
}

/** How long ago the person last used the app in any tab; null if never recorded. */
function idleFor(): number | null {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY)
  const at = raw ? parseInt(raw, 10) : NaN
  return Number.isFinite(at) ? Date.now() - at : null
}

/**
 * Sign out after an hour without use, with a five-minute warning.
 *
 * Until Oct 2026 this never fired in practice: "activity" was written every
 * ten seconds whether or not anyone touched the app, each tab reset the
 * others on every write, and a fresh load overwrote the old time — so a
 * browser reopened the next day carried on signed in. Now only real input
 * (markActivity) counts, any tab's input keeps every tab alive, and opening
 * or returning to the app after the hour signs out at once.
 */
export function useSessionTimeout(options: UseSessionTimeoutOptions = {}) {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    warningMs = WARNING_BEFORE_MS,
    onTimeout,
    onWarning,
    enabled = true,
  } = options

  const navigate = useNavigate()
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [showWarning, setShowWarning] = useState(false)
  const [remainingTime, setRemainingTime] = useState(0)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const showWarningRef = useRef(false)

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current)
      warningRef.current = null
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
  }, [])

  const handleTimeout = useCallback(() => {
    clearTimers()
    showWarningRef.current = false
    setShowWarning(false)

    pb.authStore.clear()
    showToast('Your session has expired. Please log in again.', 'info')

    // Notify other tabs to log out too
    localStorage.setItem('session_logout', Date.now().toString())
    // Clean up the flag so future sessions aren't affected
    setTimeout(() => localStorage.removeItem('session_logout'), 100)

    if (onTimeout) {
      onTimeout()
    } else {
      navigate('/login', { replace: true })
    }
  }, [clearTimers, navigate, onTimeout])

  const handleWarning = useCallback(() => {
    showWarningRef.current = true
    setShowWarning(true)
    setRemainingTime(Math.floor(warningMs / 1000))

    // Start countdown
    countdownRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current)
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)

    if (onWarning) {
      onWarning()
    }
  }, [warningMs, onWarning])

  /** Schedule the warning and the sign-out from the last recorded use, in any tab. */
  const schedule = useCallback(() => {
    if (!enabled || !pb.authStore.isValid) return
    let idle = idleFor()
    if (idle === null) {
      // Signed in before this was recorded: start the clock now.
      markActivity()
      idle = 0
    }
    if (idle >= timeoutMs) {
      handleTimeout()
      return
    }
    clearTimers()
    showWarningRef.current = false
    setShowWarning(false)
    const left = timeoutMs - idle
    if (left > warningMs) warningRef.current = setTimeout(handleWarning, left - warningMs)
    else handleWarning()
    timeoutRef.current = setTimeout(handleTimeout, left)
  }, [enabled, clearTimers, handleWarning, handleTimeout, timeoutMs, warningMs])

  const extendSession = useCallback(() => {
    markActivity()
    schedule()
    showToast('Session extended', 'success')
  }, [schedule])

  // Real input, in this tab.
  useEffect(() => {
    if (!enabled || !pb.authStore.isValid) return

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    let lastRecorded = 0
    const onInput = () => {
      // While the warning shows, only "Stay signed in" extends the session.
      if (showWarningRef.current) return
      const now = Date.now()
      if (now - lastRecorded < ACTIVITY_THROTTLE_MS) return
      lastRecorded = now
      markActivity()
      schedule()
    }
    events.forEach((event) => window.addEventListener(event, onInput, { passive: true }))

    // Opening the app: signed out at once if the hour has already passed.
    schedule()

    return () => {
      clearTimers()
      events.forEach((event) => window.removeEventListener(event, onInput))
    }
  }, [enabled, schedule, clearTimers])

  // Returning to a hidden tab (timers may not have run while it was hidden).
  useEffect(() => {
    if (!enabled) return
    const onVisible = () => {
      if (document.visibilityState === 'visible' && pb.authStore.isValid) schedule()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [enabled, schedule])

  // Other tabs: a sign-out signs this tab out; their real input keeps this one alive.
  useEffect(() => {
    if (!enabled) return

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'session_logout' && e.newValue) {
        clearTimers()
        showWarningRef.current = false
        setShowWarning(false)
        pb.authStore.clear()
        navigate('/login', { replace: true })
        return
      }
      if (e.key === LAST_ACTIVITY_KEY && e.newValue && !showWarningRef.current) {
        schedule()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [enabled, clearTimers, schedule, navigate])

  return {
    showWarning,
    remainingTime,
    extendSession,
    logout: handleTimeout,
  }
}

export default useSessionTimeout
